import {useAgendaSearchParams} from '@/features/agenda/hooks/useAgendaSearchParams'
import {useGetUsers} from '@/features/users/queries/useGetUsers'
import {useGetUserSpecializations} from '@/features/users/queries/useGetUserSpecializations'
import {UserSmall, UserSpecialization} from '@/features/users/types'
import {QueryObserverBaseResult} from '@tanstack/react-query'

export const useAgendaCategoryPatientAgeRange = (): {
    users: UserSmall[]
    specializations: UserSpecialization[]
    isLoading: QueryObserverBaseResult['isLoading']
    isError: QueryObserverBaseResult['isError']
    isSuccess: QueryObserverBaseResult['isSuccess']
    isRefetching: QueryObserverBaseResult['isRefetching']
    refetch: () => void
} => {
    const {
        searchParams: {category, specializationIds}
    } = useAgendaSearchParams()

    const isEnabledQuery =
        (category == 'evolutionary_age' || category == 'evolutionary_age_adults' || category == 'adults') &&
        !!specializationIds?.length

    const usersQuery = useGetUsers(
        {
            page: 1,
            limit: 500,
            search: ''
        },
        specializationIds
            ? {
                  filters: {
                      operator: 'and',
                      fields: [
                          {
                              key: 'specializationId',
                              operator: 'in',
                              value: specializationIds
                          },
                          {
                              key: 'patientAgeRange',
                              operator: '=',
                              value: category
                          },
                          {
                              key: 'sector',
                              operator: 'in',
                              value: ['outpatient', 'outpatient_domiciliary']
                          },
                          {
                              key: 'status',
                              operator: '=',
                              value: 'operative'
                          }
                      ]
                  }
              }
            : undefined,
        {
            enabled: isEnabledQuery
        }
    )

    const specializationsQuery = useGetUserSpecializations({
        limit: 500
    })

    const isLoading = usersQuery.isLoading || specializationsQuery.isLoading
    const isRefetching = usersQuery.isRefetching || specializationsQuery.isRefetching
    const isError = usersQuery.isError || specializationsQuery.isError
    const isSuccess = usersQuery.isSuccess || specializationsQuery.isSuccess

    return {
        users: usersQuery.data?.data ?? [],
        specializations: specializationsQuery.data?.pages.flatMap(page => page.data) ?? [],
        isLoading,
        isRefetching,
        isError,
        isSuccess,
        refetch: () => {
            usersQuery.refetch()
            specializationsQuery.refetch()
        }
    }
}
